import type { TableProps } from 'antd';
import { Switch } from 'antd';
import { AppstoreOutlined } from '@ant-design/icons';

import { DataTable } from '../../../components/shared';
import { Badge, Empty, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { GOI, ICON_KENH } from '../constants';
import { useDanhMucNenTang, useSuaDanhMuc } from '../hooks/useCanhBao';
import { useDichCanhBao } from '../hooks/useDichCanhBao';
import { THU_TU_KENH } from '../nghiep-vu/canh-bao';
import type { MaGoi, NotifyAlarmType, NotifyChannelType } from '../types';
import { VungLoi } from './VungLoi';

/**
 * Tab "Danh mục" của cổng nền tảng: bật, tắt từng loại kênh và loại cảnh báo cho toàn hệ thống, chọn gói được dùng
 * (ADR-0102 mục 2). Loại cảnh báo chưa có nguồn dữ liệu vẫn liệt kê để biết còn thiếu gì, nhưng không bật cho tenant
 * thấy được tới khi có nguồn.
 */
export function DanhMucNenTang() {
  const { t } = useT();
  const dich = useDichCanhBao();
  const dichLoi = useLoi();
  const q = useDanhMucNenTang();
  const sua = useSuaDanhMuc();
  const dangTai = q.isLoading;

  const chonGoi = (ten: string, gia: MaGoi[], onDoi: (v: MaGoi[]) => void) => (
    <Select<MaGoi[]>
      mode="multiple"
      className="w-full"
      aria-label={t('canhBaoNenTang.goiNhan', { ten })}
      value={gia}
      onChange={(v) => v.length > 0 && onDoi(v)}
      options={GOI.map((g) => ({
        value: g,
        label: t(`canhBaoMa.goi.${g}` as KhoaDich),
      }))}
    />
  );

  const cotKenh: TableProps<NotifyChannelType>['columns'] = [
    {
      title: t('canhBaoKenh.cotKenh'),
      render: (_v, row) => {
        const Icon = ICON_KENH[row.code];
        return (
          <span className="flex min-w-0 items-center gap-2">
            <Icon className="text-content-muted" />
            <span className="truncate text-sm font-medium text-content-primary">{dich.tenKenh(row.code)}</span>
          </span>
        );
      },
    },
    {
      title: t('canhBaoNenTang.cotBat'),
      width: 80,
      render: (_v, row) => (
        <Switch
          size="small"
          checked={row.enabled}
          aria-label={t('canhBaoNenTang.batNhan', {
            ten: dich.tenKenh(row.code),
          })}
          onChange={(v) => sua.mutate({ loai: 'kenh', code: row.code, du: { enabled: v } })}
        />
      ),
    },
    {
      title: t('canhBaoNenTang.cotGoi'),
      width: 360,
      render: (_v, row) =>
        chonGoi(dich.tenKenh(row.code), row.plans, (plans) =>
          sua.mutate({ loai: 'kenh', code: row.code, du: { plans } }),
        ),
    },
    {
      title: t('canhBaoNenTang.cotPhienBan'),
      width: 140,
      render: (_v, row) => <span className="font-mono text-xs text-content-muted">{row.adapter_version}</span>,
    },
  ];

  const cotLoai: TableProps<NotifyAlarmType>['columns'] = [
    {
      title: t('canhBaoNenTang.bangLoaiCanhBao'),
      render: (_v, row) => (
        <div className="flex min-w-0 max-w-sm flex-col">
          <span className="truncate text-sm font-medium text-content-primary">{dich.tenLoai(row.code)}</span>
          <span className="truncate font-mono text-2xs text-content-muted">{row.code}</span>
        </div>
      ),
    },
    {
      title: t('canhBaoNenTang.cotPhamVi'),
      width: 110,
      render: (_v, row) => (
        <Badge variant={row.scope === 'PLATFORM' ? 'violet' : 'teal'}>
          {row.scope === 'PLATFORM' ? t('canhBaoNenTang.phamViNenTang') : t('canhBaoNenTang.phamViTenant')}
        </Badge>
      ),
    },
    {
      title: t('canhBaoNenTang.cotKichHoat'),
      width: 150,
      render: (_v, row) => (
        <span className="text-xs text-content-secondary">
          {row.trigger === 'EVENT'
            ? t('canhBao.theoSuKien')
            : t('canhBaoNenTang.dinhKy', {
                phut: Math.max(1, Math.round((row.check_interval_seconds ?? 60) / 60)),
              })}
        </span>
      ),
    },
    {
      title: t('canhBaoNenTang.cotNguon'),
      width: 130,
      render: (_v, row) =>
        row.available ? (
          <Badge variant="success">{t('canhBaoNenTang.coNguon')}</Badge>
        ) : (
          <Badge variant="neutral">{t('canhBaoNenTang.chuaCoNguon')}</Badge>
        ),
    },
    {
      title: t('canhBaoNenTang.cotBat'),
      width: 80,
      render: (_v, row) => (
        <Switch
          size="small"
          checked={row.enabled}
          aria-label={t('canhBaoNenTang.batNhan', {
            ten: dich.tenLoai(row.code),
          })}
          onChange={(v) => sua.mutate({ loai: 'canh-bao', code: row.code, du: { enabled: v } })}
        />
      ),
    },
    {
      title: t('canhBaoNenTang.cotGoi'),
      width: 320,
      render: (_v, row) =>
        row.scope === 'PLATFORM'
          ? null
          : chonGoi(dich.tenLoai(row.code), row.plans, (plans) =>
              sua.mutate({ loai: 'canh-bao', code: row.code, du: { plans } }),
            ),
    },
  ];

  if (q.error && !q.data) {
    return (
      <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
        <VungLoi cau={t('canhBaoNenTang.loiDanhMuc', { loi: dichLoi(q.error) })} onThuLai={() => void q.refetch()} />
      </div>
    );
  }

  const rong = <Empty icon={AppstoreOutlined} title={t('canhBaoNenTang.rongDanhMuc')} />;
  const loaiKenh = [...(q.data?.channel_types ?? [])].sort(
    (a, b) => THU_TU_KENH.indexOf(a.code) - THU_TU_KENH.indexOf(b.code),
  );

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
        <DataTable<NotifyChannelType>
          caption={t('canhBaoNenTang.bangLoaiKenh')}
          rowKey="code"
          dataSource={loaiKenh}
          columns={cotKenh}
          loading={dangTai}
          size="middle"
          pagination={false}
          locale={{ emptyText: dangTai ? ' ' : rong }}
        />
      </div>
      <div className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm">
        <DataTable<NotifyAlarmType>
          caption={t('canhBaoNenTang.bangLoaiCanhBao')}
          rowKey="code"
          dataSource={q.data?.alarm_types ?? []}
          columns={cotLoai}
          loading={dangTai}
          size="middle"
          pagination={false}
          locale={{ emptyText: dangTai ? ' ' : rong }}
        />
      </div>
    </div>
  );
}
